// Implements: F-IDN-027, F-UIX-018, F-UIX-010
/**
 * Equipos (mockup v3 P3, `vTeams`) of one organization: a card per team the server lists for the
 * reader (every team for the org admin, only the teams they lead for a team lead, C3). Mount it
 * keyed by the organization's id (checklist 14).
 *
 * "Nuevo equipo" is offered only when the ORGANIZATION's own scope carries `team:create` (the
 * server's answer, never a role); a created team opens at once so its members can be added.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import type { BackofficeScope } from "../../../types/api";
import { NameDialog } from "../components/NameDialog";
import styles from "../components/Organization.module.css";
import { TeamCard } from "../components/TeamCard";
import { useTeamList } from "../hooks/usePeople";
import { useCreateTeam } from "../hooks/useTeams";
import { allowsAtOrganization } from "../model/targets";
import { TEAMS_PATH, writeProblem } from "./PeopleContainer";

export function teamHref(teamId: string): string {
  return `${TEAMS_PATH}/${encodeURIComponent(teamId)}`;
}

export interface TeamsContainerProps {
  readonly organizationId: string;
  readonly scopes: readonly BackofficeScope[];
}

export function TeamsContainer({ organizationId, scopes }: TeamsContainerProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teams = useTeamList(organizationId);
  const create = useCreateTeam(organizationId);
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const canCreate = allowsAtOrganization(scopes, organizationId, "team:create");

  function submit(name: string): void {
    setProblem(null);
    create.mutate(name, {
      onSuccess: (team) => {
        setCreating(false);
        void navigate(teamHref(team.id));
      },
      onError: (error) => {
        setProblem(writeProblem(asApiError(error), t("common.state.error")));
      },
    });
  }

  function body(): ReactElement {
    if (teams.isError) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(teams.error)}
          onRetry={() => {
            void teams.refetch();
          }}
        />
      );
    }
    if (teams.data === undefined) return <LoadingState label={t("organization.teams.loading")} />;
    if (teams.data.teams.length === 0) {
      return (
        <EmptyState
          headingLevel={3}
          icon="users"
          title={t("organization.teams.empty.title")}
          body={t("organization.teams.empty.body")}
        />
      );
    }
    return (
      <div className={styles.grid}>
        {teams.data.teams.map((team) => (
          <TeamCard key={team.id} team={team} href={teamHref(team.id)} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {canCreate ? (
        <div className={styles.toolbar}>
          <span className={styles.spacer} />
          <Button
            variant="primary"
            hasPopup="dialog"
            onClick={() => {
              setProblem(null);
              setCreating(true);
            }}
          >
            <Icon name="plus" size="sm" />
            {t("organization.teams.new")}
          </Button>
        </div>
      ) : null}
      {body()}
      {creating ? (
        <NameDialog
          title={t("organization.teams.new")}
          label={t("organization.teams.nameLabel")}
          submitLabel={t("organization.teams.create")}
          pending={create.isPending}
          problem={problem}
          onSubmit={submit}
          onCancel={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </div>
  );
}
